import React, { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { toast } from "sonner";
import {
  Plus, Loader2, X, Trash2, Pencil, ShieldCheck, User as UserIcon, MapPin,
} from "lucide-react";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} | user

  const load = async () => {
    setLoading(true);
    try {
      const [u, l] = await Promise.all([api.get("/users"), api.get("/locations")]);
      setUsers(u.data);
      setLocs(l.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!window.confirm(`Hapus akun ${u.name} (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Akun dihapus");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="pb-12">
      <PageHeader title="Pengaturan User" subtitle="Kelola akun admin & staff serta hak akses tempat penyimpanan">
        <button onClick={() => setModal({})} data-testid="add-user-button" className="inline-flex items-center gap-2 rounded-lg bg-teal-700 hover:bg-teal-800 px-3.5 py-2 text-sm font-semibold text-white transition-colors">
          <Plus className="h-4 w-4" /> Tambah User
        </button>
      </PageHeader>

      <div className="px-6 lg:px-8 pt-6">
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["Nama", "Email", "Role", "Hak Akses Lokasi", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid="user-row">
                    <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
                      {u.role === "admin" ? <ShieldCheck className="h-4 w-4 text-teal-600" /> : <UserIcon className="h-4 w-4 text-slate-400" />}
                      {u.name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${u.role === "admin" ? "bg-teal-50 text-teal-700" : "bg-sky-50 text-sky-700"}`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === "admin" ? (
                        <span className="text-xs text-slate-400">Semua lokasi</span>
                      ) : u.allowed_locations?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {u.allowed_locations.map((l) => (
                            <span key={l} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600"><MapPin className="h-3 w-3" />{l}</span>
                          ))}
                        </div>
                      ) : <span className="text-xs text-red-500">Belum ada akses</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => setModal(u)} className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50" data-testid="edit-user-button"><Pencil className="h-4 w-4" /></button>
                        {u.role !== "admin" && (
                          <button onClick={() => del(u)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="delete-user-button"><Trash2 className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal !== null && <UserModal user={modal} locs={locs} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function UserModal({ user, locs, onClose, onSaved }) {
  const isEdit = !!user.id;
  const [f, setF] = useState({
    name: user.name || "", email: user.email || "", password: "",
    role: user.role || "staff",
    allowed_locations: user.allowed_locations || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const toggleLoc = (nama) => {
    setF((p) => ({
      ...p,
      allowed_locations: p.allowed_locations.includes(nama)
        ? p.allowed_locations.filter((x) => x !== nama)
        : [...p.allowed_locations, nama],
    }));
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama wajib diisi"); return; }
    if (!isEdit && (!f.email.trim() || !f.password.trim())) { toast.error("Email & password wajib diisi"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/users/${user.id}`, { name: f.name, email: f.email, password: f.password || null, role: f.role, allowed_locations: f.allowed_locations });
      } else {
        await api.post("/users", { name: f.name, email: f.email, password: f.password, role: f.role, allowed_locations: f.allowed_locations });
      }
      toast.success(isEdit ? "Akun diperbarui" : "Akun staff dibuat");
      onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-head text-lg font-bold text-slate-900">{isEdit ? "Edit User" : "Tambah User"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Nama *</label>
            <input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Email {isEdit ? "" : "*"}</label>
            <input value={f.email} disabled={isEdit} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600 disabled:bg-slate-50 disabled:text-slate-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Password {isEdit ? "(kosongkan jika tidak diubah)" : "*"}</label>
            <input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="user-password-input" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Role *</label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {[["admin", "Admin", "Akses penuh ke semua menu & lokasi"], ["staff", "Staff", "Hanya lokasi yang diberi akses"]].map(([val, label, desc]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => set("role", val)}
                  data-testid={`role-option-${val}`}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${f.role === val ? "border-teal-600 bg-teal-50" : "border-slate-200 hover:bg-slate-50"}`}
                >
                  <div className={`text-sm font-semibold ${f.role === val ? "text-teal-800" : "text-slate-700"}`}>{label}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{desc}</div>
                </button>
              ))}
            </div>
          </div>
          {f.role === "staff" && (
          <div>
            <label className="text-xs font-medium text-slate-600">Hak Akses Lokasi</label>
            <div className="mt-1.5 max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {locs.length === 0 && <p className="px-3 py-3 text-xs text-slate-400">Belum ada tempat penyimpanan.</p>}
              {locs.map((l) => (
                <label key={l.id} className="flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 cursor-pointer hover:bg-slate-50" data-testid={`loc-checkbox-${l.nama}`}>
                  <input type="checkbox" checked={f.allowed_locations.includes(l.nama)} onChange={() => toggleLoc(l.nama)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                  {l.nama}
                </label>
              ))}
            </div>
          </div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Batal</button>
          <button onClick={save} disabled={saving} data-testid="save-user-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
          </button>
        </div>
      </div>
    </div>
  );
}
